import { Button, Input, SelectGray } from "components/Shared";

import { useTranslation } from "react-i18next";
import { IRequestRouterGetBankAcc, IRouterOrder } from "interfaces/router";
import { useEffect, useState } from "react";

import { useGetBankAccountsRouterQuery } from "api/institutional/banks_api";

import { useDispatch } from "react-redux";
import {
  useChangeBeneficiaryMutation,
  useChangeClientExchangeRateMutation,
} from "api/institutional/orders_api";
import {
  PaymentCategories,
  RouterSupportedDOCountry,
  RouterSupportedFiat,
} from "interfaces/enums";
import { toast } from "sonner";
import useErrorListener from "hooks/useErrorListener";
import { closeModal } from "app/slices/modalSlice";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import BigNumber from "bignumber.js";

import { hasFeeInRate } from "@/lib/utils";
import { CurrencyBody } from "@/components/Shared/CurrencyBody";
import { ChangeClientExchangeRateFormProps } from "./forms/ChangeClientExchangeRateFormProps";
import { Separator } from "@/components/ui/separator";
import EnvironmentIndicator from "@/components/EnvironmentIndicator";
import { useGetUserQuery } from "@/api/institutional/user_api";

const ChangeClientExchangeRateForm = ({
  order,
  onClose,
  onBack,
}: ChangeClientExchangeRateFormProps) => {
  const { t } = useTranslation();
  const [new_client_exchange_rate, setExchangeRate] = useState<string>("");
  const [isInverted, setIsInverted] = useState<boolean>(false);

  const [mutateExchangeRate, { error, isLoading }] =
    useChangeClientExchangeRateMutation();
  useErrorListener({ error });

  const displayedRate =
    isInverted && order.rate_client
      ? BigNumber(1).dividedBy(order.rate_client)
      : BigNumber(order.rate_client);

  const ccyPair = isInverted
    ? `${order.ccy_dest} / ${order.ccy_source}`
    : `${order.ccy_source} / ${order.ccy_dest}`;

  const handleSubmit = async () => {
    try {
      await mutateExchangeRate({
        order_router_id: order.id,
        new_client_exchange_rate: parseFloat(new_client_exchange_rate),
        rate_inverted: isInverted,
      }).unwrap();
      toast.success(t("Success"));
      onClose();
    } catch (e) {}
  };

  return (
    <>
      <div className="flex gap-2 flex-col">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {t("Current Client Exchange Rate")}
          </span>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-gray-500">{ccyPair}</span>
            <div
              className={`relative w-11 h-6 rounded-full transition-colors ${
                isInverted ? "bg-primary" : "bg-gray-100"
              }`}
              onClick={() => setIsInverted(!isInverted)}
            >
              <div
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  isInverted ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </div>
          </label>
        </div>

        {isInverted ? (
          <div className="flex font-lader text-lg py-2">
            <span className="text-primary">
              <CurrencyBody symbol={order.ccy_source} precision={8}>
                {displayedRate}
              </CurrencyBody>
            </span>
            &nbsp;{order.ccy_source} =
            <span className="pl-1"> 1 {order.ccy_dest}</span>
          </div>
        ) : (
          <div className="flex font-lader text-lg py-2">
            1 {order.ccy_source} =&nbsp;
            <span className="text-primary">
              <CurrencyBody symbol={order.ccy_source} precision={8}>
                {displayedRate}
              </CurrencyBody>
            </span>
            <span className="pl-1">{order.ccy_dest}</span>
          </div>
        )}
        {hasFeeInRate(order) ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {t("FX Rate (excl. transfer fee)")}
            </span>
            <span className="text-sm">
              <CurrencyBody symbol={order.ccy_source} precision={8}>
                {isInverted
                  ? BigNumber(1).dividedBy(order.rate_feeless!)
                  : BigNumber(order.rate_feeless!)}
              </CurrencyBody>
            </span>
          </div>
        ) : null}

        <span className="text-sm text-gray-500">{t("New Exchange Rate")}</span>
        <input
          className="border border-gray-light200 p-2 rounded-md"
          placeholder={t("Exchange Rate")}
          type="number"
          step="0.0001"
          value={new_client_exchange_rate}
          onChange={(e) => setExchangeRate(e.target.value)}
        />
      </div>

      <span className="text-sm text-gray-500">
        {t("You may only")} {isInverted ? t("increase") : t("decrease")}{" "}
        {t("this value")}
      </span>

      <Button
        disabled={isLoading || !new_client_exchange_rate}
        onClick={handleSubmit}
      >
        {t("Save")}
      </Button>
      <Button variant="outline" disabled={isLoading} onClick={onBack}>
        {t("Back")}
      </Button>
    </>
  );
};

const ChangeBeneficiaryForm = ({
  order,
  onClose,
  onBack,
}: ChangeClientExchangeRateFormProps) => {
  const { t } = useTranslation();

  const [memo, setMemo] = useState("");

  const [query, setQuery] = useState<IRequestRouterGetBankAcc>({
    approved_only: true,
  });

  const { data: user } = useGetUserQuery({});

  const isNotAllowed =
    ![RouterSupportedDOCountry.US, RouterSupportedDOCountry.MX].includes(
      order.country_dest,
    ) && order.ccy_dest !== RouterSupportedFiat.USD;

  const { data: accounts } = useGetBankAccountsRouterQuery(query);
  const [mutateBeneficiary, { error: beneficiaryError, isLoading }] =
    useChangeBeneficiaryMutation();

  useErrorListener({ error: beneficiaryError });

  const doSubmit = async () => {
    try {
      await mutateBeneficiary({
        order_router_id: order?.id!,
        payment_category: PaymentCategories.CAPITAL_INVESTMENT_OR_ITEM,
        destination_account_id: selected?.value!,
        memo,
      }).unwrap();
      toast.success(t("Success"));
      onClose();
    } catch (e) {}
  };

  useEffect(() => {
    setQuery({
      approved_only: true,
      ccy: order?.ccy_dest,
      country: order?.country_dest,
      transfer_method: order?.transfer_method,
      sub_account_id: user?.isRouterUser ? undefined : order?.user_id,
    });
  }, [order, user]);

  const mappedAccounts = accounts?.map((a) => ({
    value: a.id,
    label: a.alias,
  }));

  const [selected, setSelected] = useState<
    { value: string; label: string } | undefined
  >(
    order?.destination_account
      ? {
          value: order.destination_account.id,
          label: order.destination_account.alias,
        }
      : undefined,
  );

  if (isNotAllowed) {
    return (
      <>
        <div className="flex gap-2 flex-col">
          <span className="text-sm text-gray-500">
            {t("You can only update beneficiaries for USD payouts")}
          </span>
        </div>
        <Button variant="outline" onClick={onBack}>
          {t("Back")}
        </Button>
      </>
    );
  }

  return (
    <>
      <div className="flex gap-2 flex-col">
        <span className="text-sm text-gray-500">
          {t("Select the new destination account")}
        </span>
        <SelectGray
          placeholder={t("Select an Option")}
          value={selected}
          isSearchable
          options={mappedAccounts}
          onChange={(e: any) => {
            setSelected(e);
          }}
        />

        <span className="text-sm text-gray-500">{t("Change the memo")}</span>
        <Input
          placeholder="Memo"
          defaultValue={order?.memo}
          onChange={(e) => {
            setMemo(e?.currentTarget.value || "");
          }}
        ></Input>
      </div>

      <Button disabled={isLoading} onClick={doSubmit}>
        {t("Save")}
      </Button>
      <Button variant="outline" disabled={isLoading} onClick={onBack}>
        {t("Back")}
      </Button>
    </>
  );
};

export const EditOrderModal = ({ data: order }: { data?: IRouterOrder }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleBack = () => {
    setModal("");
  };

  const [modal, setModal] = useState<"exchange" | "beneficiary" | "">("");

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t("Edit")}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground  uppercase tracking-wide">
          {t("Order ID")}
        </span>
        <span className="text-xs text-muted-foreground font-mono truncate">
          {order?.id}
        </span>
      </div>

      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground  uppercase tracking-wide">
          {t("Beneficiary")}
        </span>
        <span className="text-xs text-muted-foreground font-mono truncate">
          {order?.destination_account?.alias} - {order?.transfer_method}
        </span>
      </div>

      <Separator />

      {modal === "exchange" && (
        <ChangeClientExchangeRateForm
          order={order!}
          onClose={handleClose}
          onBack={handleBack}
        />
      )}
      {modal === "beneficiary" && (
        <ChangeBeneficiaryForm
          order={order!}
          onClose={handleClose}
          onBack={handleBack}
        />
      )}
      {modal === "" && (
        <>
          <EnvironmentIndicator>
            <Button onClick={() => setModal("exchange")}>
              {t("Modify Client Exchange Rate")}
            </Button>
          </EnvironmentIndicator>
          <Button onClick={() => setModal("beneficiary")}>
            {t("Modify Beneficiary")} / {t("Modify Reference")}
          </Button>
          <Button variant="outline" onClick={handleClose}>
            {t("Cancel")}
          </Button>
        </>
      )}
    </DialogContent>
  );
};
